import { useCallback, useEffect, useState } from 'react';

import { keys as LocalStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const SPOTLIGHT_SEEN_LOCAL_STORAGE_KEY = LocalStorageKeys.NAVX_UC_CWR_EXPERIMENT_SPOTLIGHT_SEEN;

const hasSeen = { current: false };
const listeners = new Set<(hasSeenSpotlight: boolean) => void>();

const markSessionHasSeenSpotlight = (): boolean => {
	if (hasSeen.current) {
		return false;
	}
	hasSeen.current = true;
	return true;
};

const notifySpotlightSeen = () => {
	listeners.forEach((listener) => {
		try {
			listener(true);
		} catch {
			// ignore — a throwing listener must not prevent remaining instances from being notified
		}
	});
};

// Test-only reset.
export const __resetSessionHasSeenSpotlightForTests = (): void => {
	hasSeen.current = false;
	listeners.clear();
};

// Test-only accessor for the listeners set.
export const __getListenersForTests = (): Set<(hasSeenSpotlight: boolean) => void> => listeners;

export type UseCwRSpotlightSeenStorageResult = {
	hasSeenSpotlight: boolean;
	markSpotlightSeen: () => void;
};

export const useCwRSpotlightSeenStorage = (): UseCwRSpotlightSeenStorageResult => {
	const [hasSeenSpotlight, setHasSeenSpotlight] = useState<boolean>(true);

	useEffect(function syncFromStorage() {
		if (!hasSeen.current) {
			try {
				hasSeen.current =
					confluenceLocalStorageInstance?.getItem(SPOTLIGHT_SEEN_LOCAL_STORAGE_KEY) === 'true';
			} catch {
				hasSeen.current = false;
			}
		}

		if (!hasSeen.current) {
			setHasSeenSpotlight(false);
		}

		const listener = (nextHasSeenSpotlight: boolean) => {
			setHasSeenSpotlight(nextHasSeenSpotlight);
		};
		listeners.add(listener);

		return () => {
			listeners.delete(listener);
		};
	}, []);

	const markSpotlightSeen = useCallback(() => {
		if (!markSessionHasSeenSpotlight()) {
			return;
		}

		notifySpotlightSeen();
		try {
			confluenceLocalStorageInstance?.setItem(SPOTLIGHT_SEEN_LOCAL_STORAGE_KEY, 'true');
		} catch {
			// storage errors are ignored — in-memory flag prevents re-showing
		}
	}, []);

	return {
		hasSeenSpotlight,
		markSpotlightSeen,
	};
};
